<template>
  <FusionBaseDialog
    @close="close"
    @after-leave="afterLeave"
    :open="open"
    :error="error"
    :title="$t('shareDevice.title')"
    icon="mdi-share-variant"
    :persistentOverlay="true"
    padded
    data-cy="deviceSharingDialog"
  >
    <p v-if="deviceTypeSharingText" class="hint-text" data-cy="deviceTypeSharingText">
      {{ deviceTypeSharingText }}
    </p>
    <p v-if="hasUpTo20Collaborators" class="hint-text" data-cy="maxCollaboratorsText">
      {{ maximumNumberCollaboratorsText }}
    </p>

    <p class="hint-text" data-cy="ensureAccountInstructionText">
      {{ $t('shareDevice.ensureAccountInstruction') }}
    </p>
    <div class="d-flex flex-wrap align-center" :class="{ disabled: addDisabled }">
      <div class="collaborator-email-wrapper">
        <v-text-field
          class="collaborator-email"
          v-model="newCollaboratorEmail"
          :label="$t('shareDevice.emailLabel')"
          :rules="[rules.email]"
          data-cy="collaboratorEnterEmail"
        ></v-text-field>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        variant="text"
        class="btn-add"
        :class="{ disabled: addDisabled }"
        :disabled="addDisabled"
        @click="addCollaborator"
        data-cy="addCollaboratorButton"
        >{{ $t('buttons.add') }}
      </v-btn>
    </div>

    <v-table v-if="collaborators.length > 0" density="comfortable" class="collabTable">
      <template v-slot:default>
        <thead>
          <tr>
            <th scope="col" class="text-left">{{ $t('shareDevice.email') }}</th>
            <th scope="col" class="text-left">
              {{ $t('shareDevice.status') }}
            </th>
            <th scope="col" class="text-left">
              {{ $t('shareDevice.actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="collaborator in collaborators" :key="collaborator.email" data-cy="sharedEmail">
            <td>{{ trim(collaborator.email, 24) }}</td>
            <td data-cy="sharedStatus">
              <v-icon
                :title="
                  collaborator.status == 'active'
                    ? $t('shareDevice.collaboratorStatus.active')
                    : $t('shareDevice.collaboratorStatus.noAccount')
                "
              >
                {{ collaborator.status == 'active' ? 'mdi-check' : 'mdi-alert' }}
              </v-icon>
            </td>
            <td class="td-actions">
              <v-menu>
                <template v-slot:activator="{ props }">
                  <MenuActivator v-bind="props" data-cy="sharedActions" />
                </template>
                <v-list>
                  <v-list-item
                    :title="$t('buttons.delete')"
                    prepend-icon="mdi-delete"
                    data-cy="deleteCollaborator"
                    @click="removeCollaborator(collaborator)"
                  >
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>
    <div class="notShared hint-text" v-else>
      {{ $t('shareDevice.notShared') }}
    </div>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn
        @click="close"
        class="bg-primary text-white large"
        variant="text"
        data-cy="deviceSharingClose"
        >{{ $t('buttons.done') }}</v-btn
      >
    </template>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { checkStatus } from '@/api/services/utils'
import { SlugsEnum } from '@/permissions/SlugsEnum'
import { deviceModels } from '@/store/subscriptions/utils.js'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import type { Collaborator } from '@/types/devices'
import MenuActivator from '@/components/common/buttons/MenuActivator.vue'

const BACKEND_COLLABORATOR_LIMIT = 500
const PERMISSION_20_COLLABORATOR_NUMBER = 20
const MSG_TIMEOUT_MS = 10000
const VALID_EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/

export default defineComponent({
  name: 'DeviceSharingDialog',
  components: {
    MenuActivator
  },
  props: ['open', 'deviceId', 'permissions'],
  emits: ['close', 'afterLeave'],
  data() {
    return {
      newCollaboratorEmail: '',
      showFailedMessage: false,
      showNoAccountMessage: false,
      rules: {
        empty: (value: string) => value === null || value.length === 0,
        required: (value: string) => !!value || this.$t('validation.required'),
        email: (value: string) => {
          return value === '' || VALID_EMAIL_REGEX.test(value) || this.$t('validation.invalidEmail')
        }
      },
      collaborators: [] as Collaborator[]
    }
  },
  mounted() {
    this.init()
  },
  watch: {
    open() {
      if (this.open) {
        this.init()
      }
    }
  },
  computed: {
    hasUpTo20Collaborators(): boolean {
      return this.permissions.includes(SlugsEnum.CollaboratorsWriteLessThan21)
    },
    collaboratorLimit(): number {
      return this.hasUpTo20Collaborators
        ? PERMISSION_20_COLLABORATOR_NUMBER
        : BACKEND_COLLABORATOR_LIMIT
    },
    addDisabled(): boolean {
      return (
        !VALID_EMAIL_REGEX.test(this.newCollaboratorEmail) ||
        this.collaborators.length >= this.collaboratorLimit
      )
    },
    error(): string | undefined {
      let err

      if (this.showNoAccountMessage) {
        err = this.$t('shareDevice.userNoAccount')
      }
      if (this.showFailedMessage) {
        err = this.$t('shareDevice.failedToAdd')
      }

      return err
    },
    maximumNumberCollaboratorsText(): string {
      return this.$t('shareDevice.subtitle', {
        numberOfCollaborators: PERMISSION_20_COLLABORATOR_NUMBER
      })
    },
    isAGatewayDevice(): boolean {
      return (
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
          this.deviceId
        ) === deviceModels.SMART_BRIDGE
      )
    },
    isAConnectedDevice(): boolean {
      return !!this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID}`
      ](this.deviceId)
    },
    deviceTypeSharingText(): string {
      if (this.isAGatewayDevice) {
        return this.$t('shareDevice.deviceTypeMessage.gateway')
      }

      if (this.isAConnectedDevice) {
        return this.$t('shareDevice.deviceTypeMessage.connected')
      }

      return this.$t('shareDevice.deviceTypeMessage.other')
    }
  },
  methods: {
    init() {
      if (this.deviceId) {
        this.getCollaborators()
      }
    },
    close() {
      this.$emit('close')
    },
    afterLeave() {
      this.newCollaboratorEmail = ''
      this.$emit('afterLeave')
    },
    trim(str: string, maxlen: number) {
      if (str.length <= maxlen) return str

      str = str.substring(0, maxlen)
      return str + '...'
    },
    async getCollaborators() {
      const resp = await this.$api.getDeviceCollaborators(this.deviceId)
      if (checkStatus(resp)) {
        this.collaborators = (await resp.json()) as Collaborator[]
      }
    },
    async addCollaborator() {
      const userInfo = {
        email: this.newCollaboratorEmail.toLowerCase()
      }
      try {
        const resp = await this.$api.postShareDevice(userInfo, this.deviceId)
        this.newCollaboratorEmail = ''
        if (checkStatus(resp)) {
          const body: Collaborator = await resp.json()
          if (body && body.status === 'pending') {
            this.showNoAccountMessage = true
            setTimeout(() => {
              this.showNoAccountMessage = false
            }, MSG_TIMEOUT_MS)
          }
          this.getCollaborators()
        } else {
          this.displayFailedMessage()
        }
      } catch {
        this.displayFailedMessage()
      }
    },
    displayFailedMessage() {
      this.showFailedMessage = true
      setTimeout(() => {
        this.showFailedMessage = false
      }, MSG_TIMEOUT_MS)
    },
    async removeCollaborator(collaborator: Collaborator) {
      const resp = await this.$api.deleteDeviceCollaborator(collaborator.email, this.deviceId)
      if (checkStatus(resp)) {
        this.getCollaborators()
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.v-dialog {
  .v-input {
    padding-top: 16px;
  }

  @media (min-width: 496px) {
    @media (min-height: 770px) {
      .collabTable {
        max-height: 300px;
        overflow-y: auto;
      }
    }
  }
  @media (max-width: 496px) {
    .collabTable {
      margin-top: 1rem;
    }
  }
  .notShared {
    padding: 16px 0;
    margin-top: 24px;
    margin-bottom: 24px;
    color: rgba(0, 0, 0, 0.6);
  }
  .hint-text {
    font-size: 12px;
    line-height: 1.375;
    opacity: 0.76;
    margin-top: 0;
  }
  .v-row {
    margin-left: unset;
    margin-right: unset;
  }
  .hint-text.red-font {
    color: red;
  }
  .hint-text-button {
    margin-left: 16px;
  }
  .errorMessage {
    font-size: 12px;
    color: red;
    text-align: right;
    padding-right: 16px;
  }
  .collaborator-email-wrapper {
    width: 100% !important;
    @media (min-width: 496px) {
      width: 70% !important;
    }
  }
  .btn-add {
    @media (max-width: 496px) {
      margin-left: auto;
    }
    max-width: 104px;
    background-color: #4fb74a;
    color: #ffffff;
    min-width: 104px !important;
  }
  .btn-add.disabled {
    border-color: grey;
    background-color: #a2a2a2 !important;
  }
  .disabled {
    cursor: not-allowed;
  }
  .td-actions {
    width: 1px;
    text-align: center;
  }
  .actions-button {
    padding: 0;
    min-width: 40px;
  }
}
</style>
